<template>
  <div>
    <h1 class="sr-only">
      mulberry'ss — шовкові наволочки, аксесуари та одяг з натурального шовку
    </h1>
    <!-- Hero (вище згину) -->
    <section class="container mx-auto px-4 md:px-0 pt-6 md:pt-8 pb-6">
      <HeroBanner :slides="heroSlides" :interval="7000" />
    </section>

    <!-- Новинки — SSR (п.9б). Обгортка lazy-section лишається: вона
         економить РЕНДЕР нижче згину, але HTML віддається повним. -->
    <section class="lazy-section">
      <HomeNewProducts :items="newItems" :loading="showSkeleton" />
    </section>

    <!-- Ви переглядали (ліниве, клієнт-сайд only) -->
    <section ref="seenSentinel" class="lazy-section">
      <ClientOnly>
        <HomeRecentlyViewed v-if="showSeen" :limit="8" />
        <template #fallback>
          <HomeSkeleton :cards="8" />
        </template>
      </ClientOnly>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import {
  useAsyncData,
  useHead,
  useImage,
  useRuntimeConfig,
  useSeoMeta,
} from "#imports";
import { useElementVisibility } from "@vueuse/core";

import HeroBanner from "~/components/home/HeroBanner.vue";
import HomeNewProducts from "~/components/home/HomeNewProducts.vue";
import HomeRecentlyViewed from "~/components/home/HomeRecentlyViewed.vue";
import HomeSkeleton from "~/components/home/HomeSkeleton.vue";
import { NEW_ARRIVALS_SIZE } from "~/composables/catalog/catalogFilter";
import {
  HERO_MOBILE_WIDTHS,
  HERO_DESKTOP_WIDTHS,
  IMAGE_QUALITY,
} from "~/utils/imageSizes";

/* ====================================================================
 *  0) Новинки — на сервері. REFACTOR_PLAN п.9б.
 *
 *  Фетч живе на СТОРІНЦІ, а не в компоненті, з двох причин. Перша —
 *  механіка: `useAsyncData` без `lazy` блокує навігацію, і клік по
 *  логотипу з іншої сторінки не давав би відгуку, доки не прийдуть дані
 *  (рівно цей баг ловили в 9а, див. коментар у pages/catalog/index.vue).
 *  Лікується парою `lazy: true` + явний await під import.meta.server, а
 *  цей патерн перевірений саме на сторінці. Друга — SEO головної вже
 *  живе тут: Organization і WebSite нижче, і розтягувати JSON-LD на два
 *  файли не варто.
 * ==================================================================== */
const newArrivals = useAsyncData(
  "home-new-arrivals",
  () =>
    $fetch("/api/catalog", {
      query: { preset: "new-arrivals", limit: NEW_ARRIVALS_SIZE },
    }),
  { lazy: true },
);
const { data: newData, pending: newPending, error: newError } = newArrivals;

const newItems = computed(() => newData.value?.items ?? []);
// Скелетон лише коли показувати нічого: на першому рендері дані вже в
// HTML, тож pending одразу false і блимання немає.
const showSkeleton = computed(() => newPending.value && !newItems.value.length);

if (import.meta.server) {
  // Чекаємо явно і ЛИШЕ тут: у клієнтський бандл ця гілка не потрапляє
  // (import.meta.server згортається у false), тож навігація лишається
  // миттєвою, а сирий HTML — повним.
  await newArrivals;

  // На відміну від /catalog, збій даних НЕ віддає 503, і це рішення, а
  // не недогляд. Там товари і є сторінка, тож порожня 200 запекла б у
  // SWR брехню. Тут новинки — блок нижче згину: герой, <h1>, canonical
  // і структуровані дані від Firestore не залежать. 503 на КОРЕНІ сайту
  // через другорядний блок — сигнал незрівнянно гірший за головну без
  // однієї секції. Ціна рішення: до 60 с (swr) головна може простояти
  // без новинок; живим користувачам це добирає повтор із клієнта нижче.
}

/** 1) Дані героя (LCP) */
const runtimeConfig = useRuntimeConfig();

const allHeroSlides = [
  {
    key: "spring",
    title: "ЛИМІТОВАНА ВЕСНЯНА КОЛЕКЦІЯ",
    subtitle: "Твоя найніжніша весна з шовком <b>Mulberry'SS</b>",
    desktop: "/img/hero/slide-temp1-desktop.jpg",
    mobile: "/img/hero/slide-temp1-mobile.jpg",
    alt: "Наволочки Mulberry весняна колекція",
    link: "/catalog/pillowcases",
    ctaLabel: "Купити",
  },
  {
    key: "summer",
    title: "ЛІМІТОВАНА ЛІТНЯ КОЛЕКЦІЯ",
    subtitle: "Твоє комфортне літо з шовком <b>Mulberry'SS</b>",
    desktop: "/img/hero/slide-temp2-desktop.jpg",
    mobile: "/img/hero/slide-temp2-mobile.jpg",
    alt: "Наволочки Mulberry літня колекція",
    link: "/catalog/pillowcases",
    ctaLabel: "Купити",
  },
  {
    key: "pillowcase",
    title: "Шовкова наволочка Mulberry'SS",
    subtitle: "Наволочка, яка завжди на твоєму боці",
    desktop: "/img/hero/slide-1-desktop.jpg",
    mobile: "/img/hero/slide-1-mobile.jpg",
    alt: "Наволочки Mulberry класичні",
    link: "/catalog/pillowcases",
    ctaLabel: "Купити",
  },
  {
    key: "bedding",
    title: "Постільна білизна",
    subtitle:
      "З натурального шовку <b>під замовлення</b> </br></br> <b>Mulberry'SS</b>",
    desktop: "/img/hero/slide-2-desktop.jpg",
    mobile: "/img/hero/slide-2-mobile.jpg",
    alt: "Наволочки Mulberry постільна білизна",
    link: "/catalog/pillowcases",
    ctaLabel: "Купити",
  },
  {
    key: "accessories",
    title: "Аксесуари ",
    subtitle:
      "<b>з натурального шовку</b> - твоя легка щоденна рутина </br></br> <b>Mulberry'SS</b>",
    desktop: "/img/hero/slide-3-desktop.jpg",
    mobile: "/img/hero/slide-3-mobile.jpg",
    alt: "Наволочки Mulberry аксесуари",
    link: "/catalog?cat=hair-scrunchies,turbans,sleeping-masks,towels",
    ctaLabel: "Купити",
  },
];

function isWithinRecurringWindow(date, startMMDD, endMMDD) {
  const start = String(startMMDD || "03-01");
  const end = String(endMMDD || "05-31");

  const [startMonth, startDay] = start.split("-").map(Number);
  const [endMonth, endDay] = end.split("-").map(Number);

  if (
    !startMonth ||
    !startDay ||
    !endMonth ||
    !endDay ||
    Number.isNaN(startMonth) ||
    Number.isNaN(startDay) ||
    Number.isNaN(endMonth) ||
    Number.isNaN(endDay)
  ) {
    return false;
  }

  const currentMMDD = (date.getMonth() + 1) * 100 + date.getDate();
  const startValue = startMonth * 100 + startDay;
  const endValue = endMonth * 100 + endDay;

  // Supports both regular windows (e.g., 03-01..05-31) and cross-year windows.
  if (startValue <= endValue)
    return currentMMDD >= startValue && currentMMDD <= endValue;
  return currentMMDD >= startValue || currentMMDD <= endValue;
}

const springSeasonActive = isWithinRecurringWindow(
  new Date(),
  runtimeConfig.public.springHeroStart,
  runtimeConfig.public.springHeroEnd,
);

const showSpringHero =
  runtimeConfig.public.enableSpringHero || springSeasonActive;

const summerSeasonActive = isWithinRecurringWindow(
  new Date(),
  runtimeConfig.public.summerHeroStart,
  runtimeConfig.public.summerHeroEnd,
);

const showSummerHero =
  runtimeConfig.public.enableSummerHero || summerSeasonActive;

const heroSlides = allHeroSlides.filter((slide) => {
  if (slide.key === "spring") return showSpringHero;
  if (slide.key === "summer") return showSummerHero;
  return true;
});

/* Ширини НЕ дублюються локально: preload мусить збігатися з тим, що
   рендерить HeroBanner, інакше браузер тягне LCP двічі. Раніше списки
   тут і в компоненті розходились (1920 проти 1920+2560). */
const img = useImage();

function makeHeroSrcset(src, widths) {
  if (!src) return "";
  return widths
    .map(
      (w) =>
        `${img(src, { width: w, format: "webp", quality: IMAGE_QUALITY })} ${w}w`,
    )
    .join(", ");
}

const firstHeroSlide = heroSlides?.[0] || {};
const heroMobile = firstHeroSlide.mobile || "/img/hero/slide-1-mobile.jpg";
const heroDesktop = firstHeroSlide.desktop || "/img/hero/slide-1-desktop.jpg";

/** 2) Хінти для першого рендера */
useHead({
  link: [
    // 🔹 Мобільний LCP — preload першого слайду.
    // Пари webp+jpeg більше немає: під провайдером `vercel` модифікатор
    // format ігнорується, тож обидва хінти вказували на ОДИН URL, а
    // атрибут type при цьому брехав (віддається avif).
    {
      rel: "preload",
      as: "image",
      media: "(max-width: 767px)",
      href: img(heroMobile, {
        width: 768,
        format: "webp",
        quality: IMAGE_QUALITY,
      }),
      imagesrcset: makeHeroSrcset(heroMobile, HERO_MOBILE_WIDTHS),
      imagesizes: "100vw",
      fetchpriority: "high",
    },

    // 🔹 Десктопний LCP — preload першого слайду
    {
      rel: "preload",
      as: "image",
      media: "(min-width: 768px)",
      href: img(heroDesktop, {
        width: 1280,
        format: "webp",
        quality: IMAGE_QUALITY,
      }),
      imagesrcset: makeHeroSrcset(heroDesktop, HERO_DESKTOP_WIDTHS),
      imagesizes: "100vw",
      fetchpriority: "high",
    },

    // preconnect для Firebase (без змін)
    {
      rel: "preconnect",
      href: "https://firestore.googleapis.com",
      crossorigin: "",
    },
    {
      rel: "preconnect",
      href: "https://firebasestorage.googleapis.com",
      crossorigin: "",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "mulberryss",
        url: "https://mulberryss.shop",
        logo: "https://mulberryss.shop/og-default.jpg",
        sameAs: [
          "https://www.instagram.com/mulberry_silk_studio",
          "https://t.me/mulberry_ss",
        ],
      }),
    },
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        url: "https://mulberryss.shop",
        name: "mulberryss",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://mulberryss.shop/catalog?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      }),
    },
  ],
});

/* ====================================================================
 *  2б) SEO головної. REFACTOR_PLAN п.9б.
 *
 *  Свого title сторінка не мала: у сирий HTML їхало
 *  «mulberry’ss — інтернет-магазин — mulberry'ss», бо titleTemplate
 *  ("%s — mulberry'ss") наліплює бренд на дефолтний title, у якому
 *  бренд уже є — ще й іншим апострофом. Дефолт у nuxt.config НЕ чіпаємо:
 *  він стосується решти сторінок, і правити спільне заради однієї
 *  сторінки означало б непрохану зміну там, куди нас не просили.
 * ==================================================================== */
const TITLE = "Шовкові наволочки, аксесуари та одяг з натурального шовку";
const DESCRIPTION =
  "mulberry'ss — вироби з натурального шовку Mulberry: наволочки, тюрбани, маски для сну, гумки для волосся та одяг. Виготовлено в Україні, доставка Новою Поштою.";

const siteUrl = String(
  runtimeConfig.public?.siteUrl || "https://mulberryss.shop",
).replace(/\/$/, "");

useSeoMeta({
  title: TITLE,
  description: DESCRIPTION,
  ogTitle: TITLE,
  ogDescription: DESCRIPTION,
  ogType: "website",
  ogUrl: `${siteUrl}/`,
  twitterCard: "summary_large_image",
  twitterTitle: TITLE,
  twitterDescription: DESCRIPTION,
});

/* ItemList будується прямо по картках — на відміну від каталогу, де
   потрібен окремий прохід по slug. Тут дедуплікацію вже зробив
   selectNewArrivals: одна картка = один товар. */
const itemListLd = computed(() => {
  const entries = newItems.value
    .map((it) => ({
      url: `${siteUrl}/product/${it.product?.slug || it.product?.id || ""}`,
      name: it.product?.title || "",
    }))
    .filter((e) => !e.url.endsWith("/product/"));

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Новинки mulberry'ss",
    numberOfItems: entries.length,
    itemListElement: entries.map((e, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: e.url,
      name: e.name,
    })),
  };
});

useHead(() => ({
  script: [
    {
      key: "ld-itemlist",
      type: "application/ld+json",
      innerHTML: JSON.stringify(itemListLd.value),
    },
  ],
}));

/* 3) Ліниве монтування — лишилось тільки для «Ви переглядали»: воно
      читає localStorage, тож на сервері не існує в принципі. «Новинки»
      більше не ліниві, вони приходять готовими з сервера.

      Глобального лоадера тут теж більше немає, і це не побічна чистка.
      GlobalLoader — це bg-white/80 на весь екран, і через teleport він
      потрапляв у SSR-HTML. Лишити його після 9б означало б віддавати
      повністю відрендерену головну, накриту білим полотном до гідрації,
      тобто зробити гірше, ніж було. Лоадер існував рівно тому, що
      сторінка приїжджала порожньою. */
const seenSentinel = ref(null);
const showSeen = ref(false);
const visSeen = useElementVisibility(seenSentinel);

watch(
  visSeen,
  (v) => {
    if (v) showSeen.value = true;
  },
  { immediate: true },
);

onMounted(() => {
  // SSR не дістав новинок (Firestore лежав) — тиха повторна спроба з
  // клієнта. Користувач блок побачить, навіть якщо в кеші сторінки
  // лежить версія без нього.
  if (newError.value) newArrivals.refresh();
});
</script>

<style scoped lang="scss">
/* секції нижче згину не гідруються/не малюються поки не з’являться */
.lazy-section {
  content-visibility: auto;
  /* поставимо ближче до реальних розмірів, щоб не було стрибків */
  contain-intrinsic-size: 1000px 1400px;
  min-block-size: 520px; /* невеликий резерв висоти */
}

@media (max-width: 767px) {
  .lazy-section {
    contain-intrinsic-size: 900px 1200px;
    min-block-size: 420px;
  }
}
</style>
